// superAdminTabs.js
//
// Единый реестр SuperAdmin V2 табов. Источник правды для:
//   - числовых tab/value, которые до сих пор живут внутри SuperAdminPageV2ProjectMvp
//   - человекочитаемых slug, используемых в URL `?tab=&sub=`
//   - матрицы scope: какие табы видит superadmin vs проектный admin
//   - дефолтных табов для каждой роли
//
// При выносе card builder и финальной модульности этот файл становится «осью»
// нового тонкого shell. До тех пор MVP-страница потребляет его через
// useSuperAdminUrlTabs / useSuperAdminScope.

// Top-level табы (порядок отображения сохраняем как в текущем UI).
// Numeric value совпадает с тем, что сейчас лежит в useState(tab) внутри MVP —
// это позволяет читать/писать URL без переломов существующего state.
export const TABS = Object.freeze({
  PROJECT: { id: 'project', value: 4 },
  DEVICE: { id: 'device', value: 0 },
  LOCAL: { id: 'local', value: 1 },
  LAYOUT: { id: 'layout', value: 2 },
  USERS: { id: 'users', value: 3 },
  TELEGRAM: { id: 'telegram', value: 5 },
});

export const TAB_LIST = Object.freeze([
  TABS.PROJECT,
  TABS.DEVICE,
  TABS.LOCAL,
  TABS.LAYOUT,
  TABS.USERS,
  TABS.TELEGRAM,
]);

// Sub-табы для LOCAL и LAYOUT (порядок и числовое значение совпадают с
// существующим localTab / layoutTab state в MVP).
export const LOCAL_SUBS = Object.freeze({
  TAGS: { id: 'tags', value: 0 },
  ALARMS: { id: 'alarms', value: 1 },
  TRENDS: { id: 'trends', value: 2 },
});

export const LOCAL_SUB_LIST = Object.freeze([
  LOCAL_SUBS.TAGS,
  LOCAL_SUBS.ALARMS,
  LOCAL_SUBS.TRENDS,
]);

export const LAYOUT_SUBS = Object.freeze({
  LISTS: { id: 'lists', value: 0 },
  CARDS: { id: 'cards', value: 1 },
  SETS: { id: 'sets', value: 2 },
  GROUPS: { id: 'groups', value: 3 },
});

export const LAYOUT_SUB_LIST = Object.freeze([
  LAYOUT_SUBS.LISTS,
  LAYOUT_SUBS.CARDS,
  LAYOUT_SUBS.SETS,
  LAYOUT_SUBS.GROUPS,
]);

// Внутренние подтабы Device. Числовое value совпадает с uidTab в MVP.
export const DEVICE_SUBS = Object.freeze({
  STATIONS: { id: 'stations', value: 0 },
  TAGS: { id: 'tags', value: 1 },
  ALARMS: { id: 'alarms', value: 2 },
  TRENDS: { id: 'trends', value: 3 },
  COMMANDS: { id: 'commands', value: 4 },
  BACKUP: { id: 'backup', value: 5 },
});

export const DEVICE_SUB_LIST = Object.freeze([
  DEVICE_SUBS.STATIONS,
  DEVICE_SUBS.TAGS,
  DEVICE_SUBS.ALARMS,
  DEVICE_SUBS.TRENDS,
  DEVICE_SUBS.COMMANDS,
  DEVICE_SUBS.BACKUP,
]);

// Какие top-tabs видит каждый scope.
// catalog — мульти-проект superadmin (все табы)
// project — проектный admin. Обзор всегда виден. Device (станции) — только если выдан device.uid.
export const TAB_SCOPES = Object.freeze({
  catalog: [
    TABS.PROJECT.value,
    TABS.DEVICE.value,
    TABS.LOCAL.value,
    TABS.LAYOUT.value,
    TABS.USERS.value,
    TABS.TELEGRAM.value,
  ],
  project: [
    TABS.PROJECT.value,
    TABS.DEVICE.value,
    TABS.LOCAL.value,
    TABS.LAYOUT.value,
    TABS.USERS.value,
    TABS.TELEGRAM.value,
  ],
});

// Permission keys per top-tab. Используются и для будущего ACL,
// и для интерим-матрицы по ролям. Стиль `module.<area>` сразу
// готов к маппингу на product modules (billing).
export const TAB_PERMISSION_KEYS = Object.freeze({
  [TABS.PROJECT.value]: 'project.overview',
  [TABS.DEVICE.value]: 'device.uid',
  [TABS.LOCAL.value]: 'local',
  [TABS.LAYOUT.value]: 'layout',
  [TABS.USERS.value]: 'users',
  [TABS.TELEGRAM.value]: 'telegram',
});

// Интерим-матрица: пока сервер не отдаёт User.permissions, фронт
// сам строит capabilities из роли. Это база, поверх которой
// сервер сможет переопределять более узкие правила.
const INTERIM_ROLE_MATRIX = Object.freeze({
  superadmin: {
    tabs: '*',
    actions: '*',
  },
  admin: {
    // tab id -> { read: bool, write: bool, delete: bool }
    // Дефолт для admin без явной серверной policy — минимально достаточный
    // набор для управления людьми: только Users (rwd). Project overview,
    // Local, Layout, Telegram, Device скрыты, пока суперадмин не выдаст
    // явные права через PermissionsMatrix. См. SUPERADMIN_MODULARIZATION.md.
    tabs: {
      'project.overview': { read: false, write: false, delete: false },
      'local': { read: false, write: false, delete: false },
      'layout': { read: false, write: false, delete: false },
      'users': { read: true, write: true, delete: true },
      'telegram': { read: false, write: false, delete: false },
      'device.uid': { read: false, write: false, delete: false },
    },
  },
  user: {
    tabs: {},
  },
});

export function getInterimCapabilities(role) {
  return INTERIM_ROLE_MATRIX[role] || INTERIM_ROLE_MATRIX.user;
}

// Слияние интерим-матрицы и серверной policy (если приехала).
// Сервер «главнее» только если реально прислал tabs / actions; payload вида
// `{ role: 'admin' }` (когда колонка User.permissions ещё пустая или сама
// миграция не накатилась) НЕ должен молча перекрывать интерим — иначе
// admin начинает видеть ВСЕ табы scope-а (см. инцидент с миграцией
// `2026_05_05_project_user_command_priority_legacy_pump_tier.sql`).
export function resolveCapabilities({ role, serverCapabilities }) {
  if (
    serverCapabilities &&
    typeof serverCapabilities === 'object' &&
    (serverCapabilities.tabs !== undefined ||
      serverCapabilities.actions !== undefined ||
      serverCapabilities.quotas !== undefined)
  ) {
    return serverCapabilities;
  }
  return getInterimCapabilities(role);
}

// Возвращает массив numeric tab.value, которые видны в текущем scope+role.
export function getVisibleTabs({ scope, role, capabilities }) {
  const scopeKey = scope === 'project' ? 'project' : 'catalog';
  const baseValues = TAB_SCOPES[scopeKey];
  // Если capabilities невалидны или там нет tabs — для project scope падаем
  // на интерим-матрицу по роли (для admin это сразу даст ограниченный набор).
  // В catalog scope наоборот: superadmin без полей tabs='*' трактуется как
  // полный доступ — это безопасно, потому что в catalog-сценарии нет admin.
  let caps = capabilities;
  if (!caps || typeof caps !== 'object' || caps.tabs === undefined) {
    caps = getInterimCapabilities(role);
  }
  const withProjectHome = (values) => {
    if (scopeKey !== 'project' || values.includes(TABS.PROJECT.value)) return values;
    return [TABS.PROJECT.value, ...values];
  };
  if (caps?.tabs === '*') return withProjectHome([...baseValues]);
  if (!caps?.tabs || typeof caps.tabs !== 'object') return withProjectHome([...baseValues]);
  return withProjectHome(baseValues.filter((value) => {
    const key = TAB_PERMISSION_KEYS[value];
    const tabCaps = caps.tabs[key];
    if (!tabCaps) return false;
    return !!tabCaps.read;
  }));
}

// Доступность действия read/write/delete (для capability props табов).
// При указании sub-key проверяется конкретный подтаб (Layout: lists/cards/...,
// Local: tags/alarms/trends). Sub может переопределять родителя: write/delete
// на sub-tab не требуют write/delete на родителе, но требуют read на sub.
// Если subs не объявлены — sub-tabs наследуют родителя (обратная совместимость).
export function canAct({ capabilities, tabValue, action, sub = null }) {
  if (!capabilities) return true;
  if (capabilities.tabs === '*') return true;
  const key = TAB_PERMISSION_KEYS[tabValue];
  const tabCaps = capabilities.tabs?.[key];
  if (!tabCaps) return false;

  if (sub) {
    const subs = tabCaps.subs;
    const subCaps =
      subs && typeof subs === 'object' ? subs[sub] : null;

    if (action === 'read') {
      if (subCaps && subCaps.read !== undefined) return !!subCaps.read;
      if (subs && typeof subs === 'object') return !!tabCaps.read;
      return !!tabCaps.read;
    }

    const canReadSub = canAct({ capabilities, tabValue, action: 'read', sub });
    if (!canReadSub) return false;

    if (subCaps && subCaps[action] !== undefined) return !!subCaps[action];
    return !!tabCaps[action];
  }

  return !!tabCaps[action];
}

// Список разрешённых sub-id внутри top tab. Используется внутри
// SuperAdminPageV2ProjectMvp для скрытия скрытых подтабов в LOCAL/LAYOUT.
export function getVisibleSubs({ capabilities, tabValue }) {
  if (!capabilities || capabilities.tabs === '*') return null;
  const key = TAB_PERMISSION_KEYS[tabValue];
  const tabCaps = capabilities.tabs?.[key];
  if (!tabCaps || !tabCaps.read) return [];
  const subs = tabCaps.subs;
  if (!subs || typeof subs !== 'object') return null;

  const subIds =
    tabValue === TABS.LOCAL.value
      ? LOCAL_SUB_LIST.map((s) => s.id)
      : tabValue === TABS.LAYOUT.value
        ? LAYOUT_SUB_LIST.map((s) => s.id)
        : tabValue === TABS.DEVICE.value
          ? DEVICE_SUB_LIST.map((s) => s.id)
          : Object.keys(subs);

  const visible = subIds.filter((subId) =>
    canAct({ capabilities, tabValue, action: 'read', sub: subId }),
  );
  return visible.length ? visible : [];
}

// Удобный объект caps для прокидывания в таб-компоненты.
export function buildTabCaps(capabilities, tabValue, sub = null) {
  return {
    canRead: canAct({ capabilities, tabValue, action: 'read', sub }),
    readOnly: !canAct({ capabilities, tabValue, action: 'write', sub }),
    canCreate: canAct({ capabilities, tabValue, action: 'write', sub }),
    canDelete: canAct({ capabilities, tabValue, action: 'delete', sub }),
  };
}

// id -> tab object
/**
 * UID, которые админ может открывать.
 * allowedUidIds нет в policy — все UID проекта.
 * Массив — только эти id (пустой массив = ни одного).
 */
export function filterUidsByCapabilities(uids, capabilities) {
  const list = Array.isArray(uids) ? uids : [];
  if (!capabilities || capabilities.tabs === '*') return list;
  const raw = capabilities.allowedUidIds;
  if (!Array.isArray(raw)) return list;
  const allow = new Set(
    raw.map((id) => Number(id)).filter((id) => Number.isFinite(id)),
  );
  return list.filter((uid) => allow.has(Number(uid.id)));
}

export function getTabById(id) {
  return TAB_LIST.find((t) => t.id === id) || null;
}
export function getTabByValue(value) {
  return TAB_LIST.find((t) => t.value === value) || null;
}
export function getLocalSubById(id) {
  return LOCAL_SUB_LIST.find((t) => t.id === id) || null;
}
export function getLayoutSubById(id) {
  return LAYOUT_SUB_LIST.find((t) => t.id === id) || null;
}

// Маппинг легаси-путей в канонические `?tab=&sub=` query параметры.
// Используется компонентом LegacySuperAdminRedirect внутри App.js.
export const LEGACY_PATH_TO_TABS = Object.freeze({
  'card-builder': { tab: TABS.LAYOUT.id, sub: LAYOUT_SUBS.CARDS.id },
  'component-builder': { tab: TABS.DEVICE.id },
  'alarm-rules': { tab: TABS.LOCAL.id, sub: LOCAL_SUBS.ALARMS.id },
  'trend-configs': { tab: TABS.LOCAL.id, sub: LOCAL_SUBS.TRENDS.id },
  'code-groups': { tab: TABS.LAYOUT.id, sub: LAYOUT_SUBS.LISTS.id },
  'command-templates': { tab: TABS.DEVICE.id },
});

// Дефолтный таб при заходе в shell без `?tab=`.
export function getDefaultTab({ scope, role, capabilities }) {
  if (scope === 'project') return TABS.PROJECT.value;
  const visible = getVisibleTabs({ scope, role, capabilities });
  if (!visible.length) return TABS.USERS.value;
  // Если PROJECT виден — он дефолтный для catalog superadmin (UX как сейчас).
  if (scope === 'catalog' && visible.includes(TABS.PROJECT.value)) {
    return TABS.PROJECT.value;
  }
  // Иначе — первый доступный таб в порядке TAB_SCOPES (не хардкод users).
  return visible[0];
}

/** Query-параметры для landing URL project admin: ?tab=&sub= */
export function buildAdminTabSearchParams({ scope, role, capabilities }) {
  const defaultValue = getDefaultTab({ scope, role, capabilities });
  const tab = getTabByValue(defaultValue);
  if (!tab) return { tab: TABS.USERS.id };
  const params = { tab: tab.id };
  if (defaultValue === TABS.LOCAL.value || defaultValue === TABS.LAYOUT.value) {
    const visibleSubs = getVisibleSubs({ capabilities, tabValue: defaultValue });
    if (visibleSubs?.length) params.sub = visibleSubs[0];
  }
  return params;
}

export function buildProjectAdminLandingPath(projectSlug, { scope, role, capabilities }) {
  const params = buildAdminTabSearchParams({ scope, role, capabilities });
  const qs = new URLSearchParams(params);
  return `/p/${encodeURIComponent(projectSlug)}/admin?${qs.toString()}`;
}
